import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FacetConfigTreeSettings, SearchTab } from '@aipb/gulliver/catalog/model';
import { combineLatest, map, Observable, tap } from 'rxjs';
import { CoreFieldInfoLang, FacetTree, RegisterTreeItem, RegisterTreeResponse } from '@aipb/gulliver/castor/model';
import { CastorService, FieldRepository } from '@aipb/gulliver/castor/data-access';
import { FacetRepository, FacetService, SearchService } from '@aipb/gulliver/catalog/data-access';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FacetTreeItemComponent } from '../facet-tree-item/facet-tree-item.component';
import { MtxButtonModule } from '@ng-matero/extensions/button';
import { FacetTreeNode, TreeFacetItem } from '../../model';
import { CheckedProps, FacetTreeStore } from './facet-tree.store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FacetBaseComponent } from '../facet-base.component';

function mapToFacetTreeStructure(value: RegisterTreeResponse, parentId: string = undefined): FacetTreeNode {
  const fieldId = Object.keys(value)[0];
  const item = value[Object.keys(value)[0]];

  const items: TreeFacetItem[] = [];

  for (const i of item.facets) {
    const treeFacetItem: TreeFacetItem = {
      id: '',
      items: [],
      fieldId,
      value: undefined,
      count: undefined,
      expanded: false,
      checked: false
    };

    for (const key of Object.keys(i)) {
      if (isFinite(<number>i[key])) {
        treeFacetItem.id = parentId ? `${parentId}_${fieldId}_${key}` : `${fieldId}_${key}`;
        treeFacetItem.value = key;
        treeFacetItem.count = <number>i[key];
      } else {
        treeFacetItem.items.push(
          mapToFacetTreeStructure(
            {
              [key]: <RegisterTreeItem>i[key]
            },
            treeFacetItem.id
          )
        );
      }
    }

    items.push(treeFacetItem);
  }

  return {
    id: parentId ? `${parentId}_${fieldId}` : fieldId,
    fieldId,
    items,
    expanded: false
  };
}

function setupLimits(facets: FacetTree[]) {
  for (const facet of facets) {
    if (facet.limit === undefined) {
      facet.limit = -1
    }
    setupLimits(facet.facet);
  }

  return facets;
}

interface FacetTreeVM {
  expandedFacetTreeNodes: Record<string, boolean>;
  checkedFacetItems: CheckedProps;
}

@UntilDestroy()
@Component({
  selector: 'cat-facet-tree',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, FacetTreeItemComponent, MtxButtonModule],
  templateUrl: './facet-tree.component.html',
  styleUrl: './facet-tree.component.scss',
  providers: [FacetTreeStore]
})
export class FacetTreeComponent extends FacetBaseComponent<FacetConfigTreeSettings> implements OnInit {
  store = inject(FacetTreeStore);

  vm$: Observable<FacetTreeVM> = combineLatest({
    expandedFacetTreeNodes: this.store.expandedFacetTreeNodes$,
    checkedFacetItems: this.store.checkedFacetItems$
  });

  registerFields$: Observable<CoreFieldInfoLang[]>;

  private fieldRepository = inject(FieldRepository);
  private searchService = inject(SearchService);
  private facetService = inject(FacetService);
  private facetRepository = inject(FacetRepository);
  loading$: Observable<boolean> = this.facetRepository.loading$;
  private castor = inject(CastorService);

  ngOnInit(): void {
    this.registerFields$ = this.fieldRepository.selectRegister(this.searchItem.coreId);

    combineLatest({
      checkedFacetItems: this.store.checkedFacetItems$,
      facets: this.store.facets$
    })
      .pipe(
        untilDestroyed(this),
        tap(({ checkedFacetItems, facets }) => {
          this.searchService.updateFacetTreeResultCount(
            this.searchItem,
            this.facetService.getFacetTreeQuery(this.searchItem.id, checkedFacetItems, facets)
          );
        })
      )
      .subscribe();
  }

  hasTree(fieldId: string) {
    const env = this.settings.tree;

    return fieldId in env;
  }

  onSearch() {
    const facets = this.store.getFacets();
    const checkedFacetItems = this.store.getCheckedFacetItems();

    const query = this.facetService.getFacetTreeQuery(this.searchItem.id, checkedFacetItems, facets);

    const searchTab: SearchTab = {
      solrSearchParams: {
        query: query,
        coreId: this.searchItem.coreId,
        sort: this.searchItem.search_sort
      },
      settings: {}
    };

    this.searchService.addTab(searchTab, true);
  }

  onToggle(field: CoreFieldInfoLang) {
    const e = this.settings.tree;
    const searchItem = this.searchItem;

    if (!(field.fieldId in e)) {
      return;
    }

    if (this.store.hasFacet(field.fieldId)) {
      return;
    }

    this.castor
      .registerTree({
        registers: {
          facet: {
            fieldId: field.fieldId,
            facet: setupLimits(e[field.fieldId]),
            limit: this.settings?.facetLimit || -1
          }
        },
        query: searchItem.search.q,
        coreId: searchItem.coreId
      })
      .pipe(
        map((data) => {
          return mapToFacetTreeStructure(data);
        })
      )
      .subscribe((result) => {
        this.store.setFacet(field.fieldId, result);
      });
  }

  onCheckFacet(data: [string[], boolean], field: CoreFieldInfoLang) {
    this.store.setFacetTreeItemChecked([field.fieldId, ...data[0]], data[1]);
  }

  onExpand(field: CoreFieldInfoLang, id: string) {
    this.store.setFacetTreeNodeExpanded(id || field.fieldId, true);
    this.onToggle(field);
  }

  onCollapse(field: CoreFieldInfoLang, id: string) {
    this.store.setFacetTreeNodeExpanded(id || field.fieldId, false);
  }
}
